import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { CREDIT_PLANS, PLAN_IDS } from '../config/creditPlans';
import { RESEARCH_FEATURES } from '../config/researchFeatures';
import { OUTREACH_MODES } from '../config/outreachFeatures';
import './UpgradeAccountModal.css';

const PLAN_PRESENTATION = {
  starter: {
    label: 'Starter',
    tagline: 'Everything you need to get started.',
    icon: '/img/salesgod_light.webp',
  },
  pro: {
    label: 'Pro',
    tagline: 'Full power for your pipeline.',
    icon: '/img/globus.svg',
  },
  enterprise: {
    label: 'Max',
    tagline: 'Maximum potential unlocked.',
    icon: '/img/promax.svg',
  },
};

const PLAN_ORDER = ['starter', 'pro', 'enterprise'];

const ADDONS = [
  {
    id: 'research',
    name: 'Research Suite',
    priceEur: 69,
    icon: 'fa-magnifying-glass-chart',
    description: RESEARCH_FEATURES.map((f) => f.name).join(', ') + '.',
  },
  {
    id: 'outreach',
    name: 'Outreach Agent',
    priceEur: 49,
    icon: 'fa-rocket',
    description: OUTREACH_MODES.map((m) => m.name).join(' and ') + ' — send automatically or leave prepared drafts.',
  },
  {
    id: 'dealWizard',
    name: 'Deal Wizard',
    priceEur: 59,
    icon: 'fa-hat-wizard',
    description: 'AI-powered offers, pricing strategies and conversion-ready deals.',
  },
  {
    id: 'salesPages',
    name: 'Sales Pages',
    priceEur: 39,
    icon: 'fa-file-lines',
    description: 'AI page builder for websites, shops, funnels and digital products.',
  },
  {
    id: 'xraay',
    name: 'XRAAY',
    priceEur: 69,
    icon: 'fa-radiation',
    description: 'Business analysis with scoring, strategy and growth recommendations.',
  },
  {
    id: 'sendPlus',
    name: 'AI Send+',
    priceEur: 39,
    icon: 'fa-paper-plane',
    description: 'The Send+ button for AI-assisted sending from the lead list.',
  },
];

function formatCount(value) {
  if (value === -1) return 'Unlimited';
  return (Number(value) || 0).toLocaleString();
}

function formatEur(value) {
  return `€${(Number(value) || 0).toLocaleString('de-DE')}`;
}

function planOf(id) {
  return CREDIT_PLANS[id];
}

function planIndex(id) {
  return PLAN_ORDER.indexOf(id);
}

function normalizeCurrentPlanId(raw) {
  if (!raw) return null;
  if (CREDIT_PLANS[raw]) return raw;
  return { standard: 'starter', premium: 'pro', unlimited: 'enterprise' }[raw] || null;
}

function isLargerPlan(planId, currentPlanId) {
  const next = planIndex(planId);
  if (next < 0) return false;
  const current = planIndex(currentPlanId);
  return current < 0 || next > current;
}

const LIMIT_ROWS = [
  { key: 'credits', label: 'Credits / month', value: (p) => formatCount(p.monthlyCredits) },
  { key: 'emails', label: 'Emails / day', value: (p) => formatCount(p.features.emailsPerDay) },
  { key: 'cleans', label: 'Lead cleans / month', value: (p) => formatCount(p.features.leadsPerMonth) },
  { key: 'scans', label: 'Deep scans / month', value: (p) => formatCount(p.features.deepScansPerMonth) },
  { key: 'accounts', label: 'Email accounts', value: (p) => formatCount(p.features.emailAccounts) },
  { key: 'team', label: 'Team members', value: (p) => formatCount(p.features.teamMembers) },
];

const FLAG_ROWS = [
  {
    key: 'hyper',
    label: 'Hyper-Intelligence',
    included: (p) => !!p.features.hyperIntelligence,
  },
  {
    key: 'academy',
    label: 'Academy',
    included: (p) => !!p.features.academy,
  },
  {
    key: 'seats5',
    label: '5 team seats',
    included: (p) => (p.features.teamMembers || 0) >= 5,
  },
  {
    key: 'accounts20',
    label: '20 email accounts',
    included: (p) => (p.features.emailAccounts || 0) >= 20,
  },
  {
    key: 'academyPremium',
    label: 'Academy Premium',
    included: (p) => p.features.academy === 'premium',
  },
  {
    key: 'seats10',
    label: '10 team seats',
    included: (p) => (p.features.teamMembers || 0) >= 10,
  },
  {
    key: 'accounts40',
    label: '40 email accounts',
    included: (p) => (p.features.emailAccounts || 0) >= 40,
  },
];

function CheckMark() {
  return (
    <span className="upgrade-plans__check" aria-label="Included">
      <svg viewBox="0 0 20 20" fill="none">
        <path
          d="M5.2 10.2 8.4 13.3 14.8 6.7"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </span>
  );
}

function AbsentMark() {
  return <span className="upgrade-plans__absent" aria-label="Not included">—</span>;
}

export default function UpgradeAccountModal({ currentPlanId = null, onClose }) {
  const ids = PLAN_IDS.filter((id) => CREDIT_PLANS[id]);
  const activePlanId = normalizeCurrentPlanId(currentPlanId);
  const [step, setStep] = useState(1);
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [addonIds, setAddonIds] = useState([]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  const selectedPlan = selectedPlanId ? planOf(selectedPlanId) : null;
  const selectedPresentation = PLAN_PRESENTATION[selectedPlanId] || {};
  const selectedAddons = useMemo(
    () => ADDONS.filter((item) => addonIds.includes(item.id)),
    [addonIds]
  );
  const extrasTotal = selectedAddons.reduce((sum, item) => sum + item.priceEur, 0);
  const planTotal = selectedPlan?.priceEur || 0;
  const monthlyTotal = planTotal + extrasTotal;

  const handleSelectPlan = (id) => {
    if (!isLargerPlan(id, activePlanId)) return;
    setSelectedPlanId(id);
    setStep(2);
  };

  const toggleAddon = (id) => {
    setAddonIds((prev) => (prev.includes(id) ? prev.filter((key) => key !== id) : [...prev, id]));
  };

  const handleCheckout = () => {
    // Stripe checkout is wired here later.
  };

  return createPortal(
    <div className="upgrade-plans" onClick={onClose} role="presentation">
      <div
        className="upgrade-plans__card"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="upgrade-plans-title"
      >
        <button
          type="button"
          className="upgrade-plans__close"
          onClick={onClose}
          aria-label="Close"
        >
          <i className="fa fa-times" aria-hidden />
        </button>

        <div className="upgrade-plans__head">
          <nav className="upgrade-plans__steps" aria-label="Upgrade steps">
            <button
              type="button"
              className={`upgrade-plans__step${step === 1 ? ' is-active' : ''}`}
              onClick={() => setStep(1)}
            >
              <span>1</span> Accounts
            </button>
            <span className="upgrade-plans__stepsLine" aria-hidden />
            <button
              type="button"
              className={`upgrade-plans__step${step === 2 ? ' is-active' : ''}${!selectedPlanId ? ' is-disabled' : ''}`}
              onClick={() => selectedPlanId && setStep(2)}
              disabled={!selectedPlanId}
            >
              <span>2</span> Extras & cart
            </button>
          </nav>
          <h2 id="upgrade-plans-title" className="upgrade-plans__title">
            {step === 1 ? 'Choose a larger account' : 'Add extras'}
          </h2>
          <p className="upgrade-plans__desc">
            {step === 1
              ? 'Select a larger plan to continue. Your current plan stays as it is.'
              : 'Add optional features next to your new plan. Checkout opens Stripe.'}
          </p>
        </div>

        {step === 1 ? (
          <PlansStep
            ids={ids}
            currentPlanId={activePlanId}
            selectedPlanId={selectedPlanId}
            onSelectPlan={handleSelectPlan}
          />
        ) : (
          <CheckoutStep
            selectedPlan={selectedPlan}
            selectedPresentation={selectedPresentation}
            selectedAddons={selectedAddons}
            extrasTotal={extrasTotal}
            monthlyTotal={monthlyTotal}
            addonIds={addonIds}
            onToggle={toggleAddon}
            onBack={() => setStep(1)}
            onCheckout={handleCheckout}
          />
        )}
      </div>
    </div>,
    document.body
  );
}

function planCellClass(id, currentPlanId, selectedPlanId, extra = '') {
  const isCurrent = id === currentPlanId;
  const isPicked = id === selectedPlanId && id !== currentPlanId;
  const canPick = isLargerPlan(id, currentPlanId);
  return `upgrade-plans__cell${isCurrent ? ' is-current' : ''}${isPicked ? ' is-picked' : ''}${canPick ? ' is-pickable-col' : ''}${extra}`;
}

function PlanCell({ id, currentPlanId, selectedPlanId, onSelectPlan, extra, children }) {
  const canPick = isLargerPlan(id, currentPlanId);
  return (
    <div
      data-plan={id}
      className={planCellClass(id, currentPlanId, selectedPlanId, extra)}
      onClick={canPick ? () => onSelectPlan(id) : undefined}
      role="cell"
    >
      {children}
    </div>
  );
}

function SectionRow({ label, ids, currentPlanId, selectedPlanId, onSelectPlan, first }) {
  return (
    <div className="upgrade-plans__row" role="row">
      <div
        className={`upgrade-plans__cell upgrade-plans__cell--label upgrade-plans__cell--section${first ? ' is-first' : ''}`}
        role="rowheader"
      >
        {label}
      </div>
      {ids.map((id) => (
        <PlanCell
          key={id}
          id={id}
          currentPlanId={currentPlanId}
          selectedPlanId={selectedPlanId}
          onSelectPlan={onSelectPlan}
          extra=" upgrade-plans__cell--section"
        />
      ))}
    </div>
  );
}

function PlansStep({ ids, currentPlanId, selectedPlanId, onSelectPlan }) {
  const includedRows = FLAG_ROWS.filter((row) => ids.every((id) => row.included(planOf(id))));
  const exclusiveRows = FLAG_ROWS.filter((row) => !ids.every((id) => row.included(planOf(id))));

  return (
    <div className="upgrade-plans__table" role="table">
      <div className="upgrade-plans__row upgrade-plans__row--head" role="row">
        <div className="upgrade-plans__cell upgrade-plans__cell--label" role="columnheader" />
        {ids.map((id) => {
          const plan = planOf(id);
          const presentation = PLAN_PRESENTATION[id] || {};
          const isCurrent = id === currentPlanId;
          const canUpgrade = isLargerPlan(id, currentPlanId);
          const Tag = canUpgrade ? 'button' : 'div';
          return (
            <Tag
              key={id}
              type={canUpgrade ? 'button' : undefined}
              data-plan={id}
              className={`${planCellClass(id, currentPlanId, selectedPlanId, ' upgrade-plans__cell--plan')}${canUpgrade ? ' is-pickable' : ''}`}
              onClick={canUpgrade ? () => onSelectPlan(id) : undefined}
            >
              <span className="upgrade-plans__planName">
                {presentation.icon ? (
                  <img src={presentation.icon} alt="" className="upgrade-plans__planIcon" />
                ) : null}
                {presentation.label || plan.label}
                {isCurrent && <span className="upgrade-plans__you">Your plan</span>}
              </span>
              <span className="upgrade-plans__price">
                <span className="upgrade-plans__priceValue">{formatEur(plan.priceEur)}</span>
                <span className="upgrade-plans__priceSuffix">/ month</span>
              </span>
              <span className="upgrade-plans__tagline">{presentation.tagline}</span>
            </Tag>
          );
        })}
      </div>

      <SectionRow label="Limits" ids={ids} currentPlanId={currentPlanId} selectedPlanId={selectedPlanId} onSelectPlan={onSelectPlan} first />

      {LIMIT_ROWS.map((row) => (
        <div key={row.key} className="upgrade-plans__row" role="row">
          <div className="upgrade-plans__cell upgrade-plans__cell--label" role="rowheader">
            {row.label}
          </div>
          {ids.map((id) => (
            <PlanCell
              key={id}
              id={id}
              currentPlanId={currentPlanId}
              selectedPlanId={selectedPlanId}
              onSelectPlan={onSelectPlan}
            >
              <strong>{row.value(planOf(id))}</strong>
            </PlanCell>
          ))}
        </div>
      ))}

      <SectionRow label="Included" ids={ids} currentPlanId={currentPlanId} selectedPlanId={selectedPlanId} onSelectPlan={onSelectPlan} />

      {includedRows.map((row) => (
        <div key={row.key} className="upgrade-plans__row" role="row">
          <div className="upgrade-plans__cell upgrade-plans__cell--label" role="rowheader">
            {row.label}
          </div>
          {ids.map((id) => (
            <PlanCell
              key={id}
              id={id}
              currentPlanId={currentPlanId}
              selectedPlanId={selectedPlanId}
              onSelectPlan={onSelectPlan}
            >
              <CheckMark />
            </PlanCell>
          ))}
        </div>
      ))}

      <SectionRow label="Only on higher plans" ids={ids} currentPlanId={currentPlanId} selectedPlanId={selectedPlanId} onSelectPlan={onSelectPlan} />

      {exclusiveRows.map((row, index) => (
        <div
          key={row.key}
          className={`upgrade-plans__row${index === exclusiveRows.length - 1 ? ' upgrade-plans__row--last' : ''}`}
          role="row"
        >
          <div className="upgrade-plans__cell upgrade-plans__cell--label" role="rowheader">
            {row.label}
          </div>
          {ids.map((id) => {
            const on = row.included(planOf(id));
            return (
              <PlanCell
                key={id}
                id={id}
                currentPlanId={currentPlanId}
                selectedPlanId={selectedPlanId}
                onSelectPlan={onSelectPlan}
                extra={on ? '' : ' is-off'}
              >
                {on ? <CheckMark /> : <AbsentMark />}
              </PlanCell>
            );
          })}
        </div>
      ))}
    </div>
  );
}

function CheckoutStep({
  selectedPlan,
  selectedPresentation,
  selectedAddons,
  extrasTotal,
  monthlyTotal,
  addonIds,
  onToggle,
  onBack,
  onCheckout,
}) {
  return (
    <div className="upgrade-plans__checkout">
      <div className="upgrade-plans__addons">
        {ADDONS.map((addon) => {
          const selected = addonIds.includes(addon.id);
          return (
            <button
              key={addon.id}
              type="button"
              className={`upgrade-plans__addon${selected ? ' is-selected' : ''}`}
              onClick={() => onToggle(addon.id)}
              aria-pressed={selected}
            >
              <span className="upgrade-plans__addonTop">
                <span className="upgrade-plans__addonIcon" aria-hidden>
                  <i className={`fa ${addon.icon}`} />
                </span>
                {selected ? <CheckMark /> : <span className="upgrade-plans__addonPick" aria-hidden />}
              </span>
              <span className="upgrade-plans__addonName">{addon.name}</span>
              <span className="upgrade-plans__addonPrice">
                {formatEur(addon.priceEur)}
                <span> / month</span>
              </span>
              <span className="upgrade-plans__addonDesc">{addon.description}</span>
            </button>
          );
        })}
      </div>

      <aside className="upgrade-plans__cart" aria-label="Cart">
        <h3 className="upgrade-plans__cartTitle">Your order</h3>
        <ul className="upgrade-plans__cartList">
          <li className="upgrade-plans__cartLine">
            <span>
              {selectedPresentation.label || selectedPlan?.label}
              <em>Account</em>
            </span>
            <strong>{formatEur(selectedPlan?.priceEur)}</strong>
          </li>
          {selectedAddons.map((addon) => (
            <li key={addon.id} className="upgrade-plans__cartLine">
              <span>
                {addon.name}
                <button type="button" onClick={() => onToggle(addon.id)}>
                  Remove
                </button>
              </span>
              <strong>{formatEur(addon.priceEur)}</strong>
            </li>
          ))}
        </ul>
        {extrasTotal > 0 && (
          <div className="upgrade-plans__cartSub">
            <span>Extras</span>
            <span>{formatEur(extrasTotal)}</span>
          </div>
        )}
        <div className="upgrade-plans__cartTotal">
          <span>Monthly total</span>
          <strong>{formatEur(monthlyTotal)}</strong>
        </div>
        <button type="button" className="upgrade-plans__cta upgrade-plans__cta--block" onClick={onCheckout}>
          Continue to checkout
        </button>
        <button type="button" className="upgrade-plans__ghost" onClick={onBack}>
          Change plan
        </button>
      </aside>
    </div>
  );
}
